var App = App || {};

App.PrimaryNav = (function () {
    "use strict";

    // Variables for DOM elements and state
    let primaryNavElement;
    const onTimer = {}; // Stores timeouts for opening menus
    const offTimer = {}; // Stores timeouts for closing menus
    let focusList = []; // List of focusable elements in the current open menu
    let focusIndex = 0; // Current index in the focusList
    let currentMenuParentItem = null; // The <li> element of the currently open top-level menu

    /**
     * Initializes the primary navigation module.
     * Sets up event listeners for keyboard navigation, mouse interactions, and outside clicks.
     */
    function init() {
        primaryNavElement = document.getElementById('primary-nav');

        if (!primaryNavElement) {
            return;
        }

        // Global keydown listener for keyboard navigation (Escape, Arrow keys)
        window.addEventListener('keydown', (e) => {
            // Check for Escape key to close the current menu
            if (e.key === 'Escape' && currentMenuParentItem) {
                e.preventDefault();
                closeMenu(currentMenuParentItem);
                // Return focus to the parent link after closing
                currentMenuParentItem.querySelector(':scope > a').focus();
            }

            // Prevent default for arrow keys if a menu is open, to handle focus manually
            if (currentMenuParentItem && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                // Arrow key handling is delegated to the nav element listeners below
                e.preventDefault(); // Prevent page scrolling
            }
        });

        // Event delegation for keyboard navigation on top-level items with children
        primaryNavElement.addEventListener('keydown', (e) => {
            const targetLink = e.target.closest('.primary-nav__level-1-item--has-children > a');
            if (!targetLink) return;

            const parentItem = targetLink.closest('li');

            // Enter key to open menu if not already open
            if (!parentItem.classList.contains('primary-nav__level-1-item--open') && e.key === 'Enter') {
                e.preventDefault();
                openMenu(parentItem);
            }

            // Left arrow key
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                // Find the previous sibling <li> that is a top-level item and focus its child <a>
                const prevSibling = parentItem.previousElementSibling;
                if (prevSibling && prevSibling.classList.contains('primary-nav__level-1-item')) {
                    prevSibling.querySelector(':scope > a').focus();
                }
            }

            // Right arrow key
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                // Find the next sibling <li> that is a top-level item and focus its child <a>
                const nextSibling = parentItem.nextElementSibling;
                if (nextSibling && nextSibling.classList.contains('primary-nav__level-1-item')) {
                    nextSibling.querySelector(':scope > a').focus();
                }
            }

            // Down arrow key (only if menu is open)
            if (parentItem.classList.contains('primary-nav__level-1-item--open') && e.key === 'ArrowDown') {
                e.preventDefault();
                // If the menu is open, shift focus to the first item in the sub-menu
                if (focusList.length > 0) {
                    focusList[0].focus();
                }
            }
        });

        // Event delegation for keyboard navigation within sub-menus
        primaryNavElement.addEventListener('keydown', (e) => {
            const targetMenuItem = e.target.closest('.primary-nav__menu a');
            if (!targetMenuItem) return;

            const parentItem = primaryNavElement.querySelector('.primary-nav__level-1-item--open');
            if (!parentItem) return; // Should only happen if a menu is open

            // Down arrow key within menu
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (focusIndex < focusList.length - 1) {
                    focusIndex++;
                }
                focusList[focusIndex].focus();
            }

            // Up arrow key within menu
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (focusIndex > 0) {
                    focusIndex--;
                } else {
                    // If at the first item, move focus back to the parent top-level link
                    parentItem.querySelector(':scope > a').focus();
                    closeMenu(parentItem); // Optionally close the menu
                    return; // Stop further processing to prevent moving to previous focusList item
                }
                focusList[focusIndex].focus();
            }

            // Left arrow key (from sub-menu item back to previous top-level link)
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                const prevSibling = parentItem.previousElementSibling;
                if (prevSibling && prevSibling.classList.contains('primary-nav__level-1-item')) {
                    prevSibling.querySelector(':scope > a').focus();
                    closeMenu(parentItem); // Close current menu when moving to another top-level item
                }
            }

            // Right arrow key (from sub-menu item to next top-level link)
            if (e.key === 'ArrowRight') {
                e.preventDefault();
                const nextSibling = parentItem.nextElementSibling;
                if (nextSibling && nextSibling.classList.contains('primary-nav__level-1-item')) {
                    nextSibling.querySelector(':scope > a').focus();
                    closeMenu(parentItem); // Close current menu when moving to another top-level item
                }
            }
        });

        // Mouseenter and focusin events for opening menus with a slight delay
        // Targets both the top-level link and the menu itself for continuous hover/focus
        primaryNavElement.querySelectorAll('.primary-nav__level-1-item--has-children > a, .primary-nav__level-1-item > .primary-nav__menu').forEach(element => {
            element.addEventListener('mouseenter', handleMouseEnterFocusIn);
            element.addEventListener('focusin', handleMouseEnterFocusIn);
        });

        // Mouseleave and focusout events for closing menus with a slight delay
        primaryNavElement.querySelectorAll('.primary-nav__level-1-item--has-children').forEach(element => {
            element.addEventListener('mouseleave', handleMouseLeaveFocusOut);
            element.addEventListener('focusout', handleMouseLeaveFocusOut);
        });

        // Handle clicks outside the navigation to close open menus
        document.body.addEventListener('click', handleOutsideClick);
    }

    /**
     * Handles mouseenter and focusin events to open menus.
     * @param {Event} e - The event object.
     */
    function handleMouseEnterFocusIn(e) {
        // Find the direct parent <li> element that has children
        const parentItem = e.target.closest('.primary-nav__level-1-item--has-children');
        if (!parentItem) return;

        const key = parentItem.querySelector(':scope > a').innerText;

        clearTimeout(offTimer[key]); // Clear any pending 'off' timer

        // Only open if not already open to avoid re-triggering animation/logic
        if (!parentItem.classList.contains('primary-nav__level-1-item--open')) {
            onTimer[key] = setTimeout(() => openMenu(parentItem), 100);
        }
    }

    /**
     * Handles mouseleave and focusout events to close menus.
     * @param {Event} e - The event object.
     */
    function handleMouseLeaveFocusOut(e) {
        // Ensure relatedTarget is null or outside the navigation hierarchy
        // This prevents the menu from closing when moving focus/mouse within the same menu or to a child
        if (e.relatedTarget && primaryNavElement.contains(e.relatedTarget)) {
            return;
        }

        const parentItem = e.target.closest('.primary-nav__level-1-item--has-children');
        if (!parentItem) return;

        const key = parentItem.querySelector(':scope > a').innerText;

        clearTimeout(onTimer[key]); // Clear any pending 'on' timer

        // Only close if currently open
        if (parentItem.classList.contains('primary-nav__level-1-item--open')) {
            offTimer[key] = setTimeout(() => closeMenu(parentItem), 450);
        }
    }

    /**
     * Handles clicks anywhere on the document body. If the click is outside
     * the current open menu and the main navigation, it closes the menu.
     * @param {Event} e - The click event object.
     */
    function handleOutsideClick(e) {
        // If there's an open menu and the click target is not inside the primary navigation itself
        // (which includes the current menu and its parent item)
        if (currentMenuParentItem && !primaryNavElement.contains(e.target)) {
            closeMenu(currentMenuParentItem);
        }
    }

    /**
     * Opens a given menu item.
     * @param {HTMLElement} item - The <li> element of the top-level menu item to open.
     */
    function openMenu(item) {
        const menu = item.querySelector('.primary-nav__menu');
        if (!menu) return; // Ensure there's a menu to open

        // Reset focus list and index for the new menu
        focusList = [];
        focusIndex = 0;

        // Close any currently open menu if it's different from the one we're opening
        if (currentMenuParentItem && currentMenuParentItem !== item) {
            closeMenu(currentMenuParentItem);
        }

        // Populate focusList with all focusable links within the menu
        // This will correctly pick up links at all levels (level-2, level-3, quick links)
        menu.querySelectorAll('a').forEach(anchor => {
            focusList.push(anchor);
        });

        // Add 'primary-nav__level-1-item--open' class to the parent <li>
        item.classList.add('primary-nav__level-1-item--open');
        // Set aria-expanded attribute on the parent <a> link for accessibility
        item.querySelector(':scope > a').setAttribute('aria-expanded', 'true');

        // Add 'd-grid' class to the menu for display
        menu.classList.add('d-grid');

        // Set the currently open menu parent item
        currentMenuParentItem = item;
    }

    /**
     * Closes a given menu item.
     * @param {HTMLElement} item - The <li> element of the top-level menu item to close.
     */
    function closeMenu(item) {
        const menu = item.querySelector('.primary-nav__menu');
        if (!menu) return; // Ensure there's a menu to close

        // Remove 'primary-nav__level-1-item--open' class
        item.classList.remove('primary-nav__level-1-item--open');
        // Set aria-expanded attribute back to 'false'
        item.querySelector(':scope > a').setAttribute('aria-expanded', 'false');

        // Remove 'd-grid' class to hide the menu
        menu.classList.remove('d-grid');

        // If no other top-level menus are open, reset currentMenuParentItem
        // This checks if any sibling <li> elements (at the same level) still have the 'open' class
        const hasOtherOpenSiblings = Array.from(item.parentNode.children).some(
            child => child !== item && child.classList.contains('primary-nav__level-1-item--open')
        );

        if (!hasOtherOpenSiblings) {
            currentMenuParentItem = null;
        }
    }

    // Public interface
    return {
        init: init
    };
})();

document.addEventListener('DOMContentLoaded', () => {
    App.PrimaryNav.init();
});